import { createSelector } from '@reduxjs/toolkit';

/**
 * Memoized selector that combines all Cards page data.
 * Prevents unnecessary re-renders by only updating when actual data changes.
 */
export const selectCardsPageContext = createSelector(
  [
    (state: any) => state.cardsStore,
    (state: any) => state.userStore,
    (state: any) => state.getVehicleTypes,
    (state: any) => state.setSingleCardStatus,
    (state: any) => state.setMultipleCardStatus,
    (state: any) => state.activateCard,
    (state: any) => state.activateMultipleCards,
    (state: any) => state.cardDetailView,
    (state: any) => state.editMultipleCardDetail,
    (state: any) => state.cardUsageOptions,
  ],
  (
    cardsStore,
    userStore,
    vehicleTypesStore,
    setSingleCardStatus,
    setMultipleCardStatus,
    activateCard,
    activateMultipleCards,
    cardDetailView,
    editMultipleCardDetail,
    cardUsageOptions
  ) => ({
    // Cards data
    cardResults: cardsStore.cards,
    cardResultsSize: cardsStore.totalSize,

    // Cards loading states
    isCardsLoading: cardsStore.isLoading,
    isCardsLoaded: cardsStore.isLoaded,
    isCardsError: cardsStore.isError,
    isCardNumberAdded: cardsStore.isCardNumberAdded,

    // User context
    loggedInUser: userStore.loggedInUser,
    assignedAccountNumber: userStore.assignedAccountNumber,
    assignedGrandfatherClientNumber: userStore.assignedGrandfatherClientNumber,
    canReadWrite: userStore.canReadWriteCards,

    // Vehicle types
    vehicleTypes: vehicleTypesStore.vehicleTypes,

    // Card status updates
    updateSingleCardStatusLoaded: setSingleCardStatus.success,
    updateMultipleCardStatusLoaded: setMultipleCardStatus.success,

    // Card activation
    activateSingleCardLoaded: activateCard.activateCardSuccess,
    activateMultipleCardLoaded: activateMultipleCards.activateCardSuccess,

    // Card CRUD operations
    createCardLoaded: cardsStore.createCardLoaded,
    editCardLoaded: cardDetailView.editCardLoaded,
    editMultipleCardLoaded: editMultipleCardDetail.success,

    // Card transfers
    transferSingleCardLoaded: cardsStore.singleCardTransferLoaded,
    transferMultipleCardLoaded: cardsStore.batchCardTransferLoaded,

    // Global search
    globalSearchLoaded: cardsStore.globalSearchLoaded,
    globalSearchResult: cardsStore.globalSearchResult,
    globalSearchType: cardsStore.globalSearchType,

    // DataTable loading state (composite of all loading states)
    isDataTableLoading:
      cardsStore.isLoading ||
      cardsStore.updatePinLoading ||
      cardsStore.resetPinIsLoading ||
      activateCard.isLoading ||
      activateMultipleCards.isLoading ||
      cardsStore.batchCardTransferLoading ||
      cardsStore.singleCardTransferLoading ||
      editMultipleCardDetail.isLoading ||
      cardDetailView.editCardLoading ||
      cardsStore.createCardLoading ||
      setMultipleCardStatus.isLoading ||
      setSingleCardStatus.isLoading ||
      cardUsageOptions.isLoading,
  })
);

/**
 * Memoized selector for CardsList component.
 *
 * @remarks
 * Combines data from 5 store slices: cardsStore, userStore, cardDetailView,
 * modalStore, and activateCard for the cards-list page component.
 */
export const selectCardsListPageContext = createSelector(
  [
    (state: any) => state.cardsStore,
    (state: any) => state.userStore,
    (state: any) => state.cardDetailView,
    (state: any) => state.modalStore,
    (state: any) => state.activateCard,
  ],
  (cardsStore, userStore, cardDetailView, modalStore, activateCard) => ({
    // User permissions and identity
    canReadWrite: userStore.canReadWriteCards,
    assignedGrandfatherClientNumber: userStore.assignedGrandfatherClientNumber,
    assignedAccountNumber: userStore.assignedAccountNumber,
    loggedInUser: userStore.loggedInUser,

    // Card detail view
    cardDetail: cardDetailView.cardDetail,
    cardDetailLoading: cardDetailView.isLoading,
    isPreviousOrReplacementCardClicked: cardDetailView.isPreviousOrReplacementCardClicked,
    previousOrReplacementCardNumber: cardDetailView.previousOrReplacementCardNumber,

    // Modal actions
    primaryActionButton: modalStore.primaryActionButton,
    secondaryActionButton: modalStore.secondaryActionButton,

    // Card activation
    activateCardLoading: activateCard.isLoading,
    activateCardLoaded: activateCard.activateCardSuccess,

    // Card creation and transfers
    pendingTransferCount: cardsStore.pendingCardsSize,
    createdCardNumber: cardsStore.createCardNumber,
    showCreatedCardModal: cardsStore.showCreatedCardModal,
    isCardNumberAdded: cardsStore.isCardNumberAdded,

    // Global search
    isGlobalSearchLoaded: cardsStore.globalSearchLoaded,
  })
);
